import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({
        type: 'success',
        message: response.data.message || 'Pesan Anda telah berhasil dikirim!'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Terjadi kesalahan. Silakan coba lagi.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-10"
      data-testid="contact-form"
    >
      <div className="mb-8">
        <h3 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-2">
          Kirim Pesan
        </h3>
        <p className="text-slate-600">
          Isi form di bawah ini dan tim kami akan segera menghubungi Anda.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-700 font-medium">
              Nama Lengkap <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Masukkan nama lengkap"
              className="rounded-xl border-slate-200 focus:border-[#001582] focus:ring-[#001582]/20"
              data-testid="contact-name"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700 font-medium">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="email@perusahaan.com"
              className="rounded-xl border-slate-200 focus:border-[#001582] focus:ring-[#001582]/20"
              data-testid="contact-email"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-slate-700 font-medium">
              Nomor Telepon
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="08xxxxxxxxxx"
              className="rounded-xl border-slate-200 focus:border-[#001582] focus:ring-[#001582]/20"
              data-testid="contact-phone"
            />
          </div>

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="company" className="text-slate-700 font-medium">
              Perusahaan
            </Label>
            <Input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              placeholder="Nama perusahaan Anda"
              className="rounded-xl border-slate-200 focus:border-[#001582] focus:ring-[#001582]/20"
              data-testid="contact-company"
            />
          </div>
        </div>

        {/* Subject */}
        <div className="space-y-2">
          <Label htmlFor="subject" className="text-slate-700 font-medium">
            Subjek <span className="text-red-500">*</span>
          </Label>
          <Input
            id="subject"
            name="subject"
            type="text"
            value={formData.subject}
            onChange={handleChange}
            required
            placeholder="Perihal pesan Anda"
            className="rounded-xl border-slate-200 focus:border-[#001582] focus:ring-[#001582]/20"
            data-testid="contact-subject"
          />
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label htmlFor="message" className="text-slate-700 font-medium">
            Pesan <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            placeholder="Jelaskan kebutuhan Anda secara detail..."
            rows={5}
            className="rounded-xl border-slate-200 focus:border-[#001582] focus:ring-[#001582]/20 resize-none"
            data-testid="contact-message"
          />
        </div>

        {/* Submit Status */}
        {submitStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-3 p-4 rounded-xl ${
              submitStatus.type === 'success' 
                ? 'bg-green-50 text-green-700' 
                : 'bg-red-50 text-red-700'
            }`}
            data-testid="contact-status"
          >
            {submitStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm font-medium">{submitStatus.message}</p>
          </motion.div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-[#001582] to-[#001ea7] text-white hover:from-[#001ea7] hover:to-[#001582] font-semibold py-6 rounded-xl"
          data-testid="contact-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Mengirim...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Kirim Pesan
            </>
          )}
        </Button>
      </form>
    </motion.div>
  );
};

export default ContactForm;
